import { useEffect, useState } from "react";
import {
  Stack,
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Typography,
  Button,
  Switch,
  Select,
  MenuItem,
  Autocomplete,
} from "@mui/material";
import { OrganAtRiskOut } from "@providers/hop-ord-server/api";
import { AddCircleOutlined, RemoveCircle } from "@mui/icons-material";

const LESS_THAN_OR_EQUAL = "≤";
const GREATER_THAN = ">";

const CONSTRAINT_OPERATOR_OPTIONS = [LESS_THAN_OR_EQUAL, "<"];

const ALT_VALUE_UNIT_OPTIONS = ["%", "cc"];

type OrganAtRiskConstraint = {
  id: number;
  operator: string;
  firstValue?: string | null;
  secondValue?: string[] | null;
  isVolumeData?: boolean | null;
  firstValueUnit: string;
  secondValueUnit: string;
  prefixOperator?: string | null;
};

type OrganAtRisk = {
  id: number;
  sortIndex: number;
  siteGroupId: number;
  alara?: boolean | null;
  organ: string;
  maxValue?: string[] | null;
  meanValue?: string[] | null;
  oarConstraints?: OrganAtRiskConstraint[] | null;
  constraints?: string | null;
  maxValueUnit?: string | null;
  meanValueUnit?: string | null;
  createdAt: string;
};

const EMPTY_ORGAN: OrganAtRisk = {
  id: 0,
  sortIndex: 0,
  siteGroupId: 0,
  organ: "",
  oarConstraints: [],
  alara: false,
  maxValue: [],
  meanValue: [],
  maxValueUnit: "",
  meanValueUnit: "",
  createdAt: "",
};

const EMPTY_CONSTRAINT: OrganAtRiskConstraint = {
  id: -1,
  firstValue: null,
  firstValueUnit: "",
  operator: "",
  secondValue: null,
  secondValueUnit: "",
  isVolumeData: false,
  prefixOperator: "",
};

const LABEL_WIDTH = 129;
const UNIT_WIDTH = 80;

const cleanOarConstraintData = (
  constraint: OrganAtRiskConstraint,
): OrganAtRiskConstraint => {
  return {
    ...constraint,
    operator:
      constraint.operator?.replace(/&gt;/g, ">").replace(/&lt;/g, "<") || "",
    firstValueUnit:
      constraint.firstValueUnit?.replace(/&gt;/g, ">").replace(/&lt;/g, "<") ||
      "",
    secondValueUnit:
      constraint.secondValueUnit?.replace(/&gt;/g, ">").replace(/&lt;/g, "<") ||
      "",
    prefixOperator:
      constraint.prefixOperator?.replace(/&gt;/g, ">").replace(/&lt;/g, "<") ||
      "",
  };
};

const convertValue = (value: string): string[] => {
  // Strip all non-numeric values other than "/"
  const strippedValue = value.replace(/[^0-9./]/g, "");
  if (strippedValue.includes("/")) {
    const values = strippedValue
      .split("/")
      .map((val) => (isNaN(Number(val)) ? null : val.toString()));
    return values.filter((val) => val !== null) as string[];
  }
  return isNaN(Number(strippedValue)) ? [] : [strippedValue.toString()];
};

const convertConstraintValue = (
  field: string,
  value: string,
): string | string[] => {
  if (field === "prefixOperator" && value === "None") {
    return "";
  }
  if (field === "secondValue") {
    return convertValue(value);
  }
  if (field === "firstValue") {
    // Strip all non-numeric values including "/"
    const strippedValue = value.replace(/[^0-9.]/g, "");
    return isNaN(Number(strippedValue)) ? "" : strippedValue;
  }
  return value;
};

const constraintIsNull = (constraint: OrganAtRiskConstraint) => {
  return (
    constraint.firstValue === null &&
    constraint.firstValueUnit === "" &&
    constraint.operator === "" &&
    constraint.secondValue === null &&
    constraint.secondValueUnit === ""
  );
};

interface OarConstraintRowProps {
  index: number;
  isVolume: boolean;
  constraint: OrganAtRiskConstraint;
  disabled: boolean;
  onChange: (field: string, value: string) => void;
  doseUnit: string;
  showPrefixOperator: boolean;
}

const OarConstraintRow = ({
  index,
  isVolume,
  constraint,
  disabled,
  onChange,
  doseUnit,
  showPrefixOperator,
}: OarConstraintRowProps) => {
  const VALUE_UNIT_OPTIONS = [doseUnit, "%"];

  const secondValue = Array.isArray(constraint?.secondValue)
    ? constraint?.secondValue?.join("/")
    : [constraint?.secondValue];
  const constraintType = isVolume ? "volume" : "dose";
  const firstValueUnitOptions = isVolume
    ? VALUE_UNIT_OPTIONS
    : ALT_VALUE_UNIT_OPTIONS;
  const secondValueUnitOptions = isVolume
    ? ALT_VALUE_UNIT_OPTIONS
    : VALUE_UNIT_OPTIONS;
  return (
    <Stack
      direction="row"
      sx={{
        gap: 2,
      }}
    >
      {showPrefixOperator && (
        <Select
          value={constraint?.prefixOperator || ""}
          data-testid={`prefix-operator-${constraintType}-${index}`}
          size="small"
          disabled={disabled}
          onChange={(e) => onChange("prefixOperator", e.target.value)}
          sx={{ marginLeft: -9.4, minWidth: UNIT_WIDTH }}
        >
          <MenuItem value="None">None</MenuItem>
          <MenuItem value={GREATER_THAN}>{GREATER_THAN}</MenuItem>
        </Select>
      )}
      <TextField
        value={constraint?.firstValue}
        data-testid={`first-value-${constraintType}-${index}`}
        size="small"
        disabled={disabled}
        onChange={(e) => onChange("firstValue", e.target.value)}
      />
      <Select
        value={constraint?.firstValueUnit}
        data-testid={`first-value-unit-${constraintType}-${index}`}
        size="small"
        disabled={disabled}
        onChange={(e) => onChange("firstValueUnit", e.target.value)}
        sx={{ minWidth: UNIT_WIDTH }}
      >
        {firstValueUnitOptions.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
      <Select
        value={constraint?.operator}
        data-testid={`operator-${constraintType}-${index}`}
        size="small"
        disabled={disabled}
        onChange={(e) => onChange("operator", e.target.value)}
        sx={{ minWidth: UNIT_WIDTH }}
      >
        {CONSTRAINT_OPERATOR_OPTIONS.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
      <TextField
        value={secondValue}
        data-testid={`second-value-${constraintType}-${index}`}
        disabled={disabled}
        size="small"
        onChange={(e) => onChange("secondValue", e.target.value)}
      />
      <Select
        value={constraint?.secondValueUnit}
        data-testid={`second-value-unit-${constraintType}-${index}`}
        size="small"
        disabled={disabled}
        onChange={(e) => onChange("secondValueUnit", e.target.value)}
        sx={{ minWidth: UNIT_WIDTH }}
      >
        {secondValueUnitOptions.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </Stack>
  );
};

interface OarConstraintProps {
  oar: OrganAtRisk;
  onChange: (oar: OrganAtRisk) => void;
  disabled: boolean;
  doseUnit: string;
}

const OarConstraint = ({
  oar,
  onChange,
  disabled,
  doseUnit,
}: OarConstraintProps) => {
  const volumeConstraints = oar?.oarConstraints?.filter(
    (constraint: OrganAtRiskConstraint) => constraint?.isVolumeData,
  );
  const dConstraints = oar?.oarConstraints?.filter(
    (constraint: OrganAtRiskConstraint) => !constraint?.isVolumeData,
  );
  const handleChange = (index: number, field: string, value: string) => {
    // Recreate the constraints array with the updated value
    const updatedValue = convertConstraintValue(field, value);

    // Sanity check to make sure the constraint actually exists
    if (!oar.oarConstraints?.[index]) {
      return;
    }

    const updatedConstraint = {
      ...oar.oarConstraints?.[index],
      [field]: updatedValue,
    };
    const updatedOar = {
      ...oar,
      oarConstraints: oar.oarConstraints?.map(
        (constraint: OrganAtRiskConstraint, i: number) =>
          i === index ? updatedConstraint : constraint,
      ),
    };
    onChange(updatedOar);
  };

  const handleRemove = (index: number) => {
    // Remove the constraint
    const updatedConstraints = oar.oarConstraints?.filter(
      (_, i: number) => i !== index,
    );
    const updatedOar = {
      ...oar,
      oarConstraints: updatedConstraints,
    };
    onChange(updatedOar);
  };

  const handleAdd = (isVolumeData: boolean) => {
    const newConstraint = {
      ...EMPTY_CONSTRAINT,
      isVolumeData,
    };
    const updatedOar = {
      ...oar,
      oarConstraints: [...(oar.oarConstraints || []), newConstraint],
    };
    onChange(updatedOar);
  };

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <Stack
        direction="row"
        sx={{
          gap: 2,
        }}
      >
        <Typography
          sx={{
            minWidth: LABEL_WIDTH,
            paddingTop: 1,
          }}
        >
          Volume
        </Typography>
        <Stack
          sx={{
            gap: 2,
          }}
        >
          {volumeConstraints?.map((constraint: OrganAtRiskConstraint) => {
            const index = oar.oarConstraints?.indexOf(constraint) || 0;
            return (
              <Stack
                direction="row"
                key={`${index}-volume-constraint`}
                sx={{
                  gap: 2,
                }}
              >
                <OarConstraintRow
                  key={index}
                  index={index}
                  isVolume={true}
                  constraint={constraint}
                  disabled={disabled}
                  onChange={(field: string, value: string) =>
                    handleChange(index, field, value)
                  }
                  doseUnit={doseUnit}
                  showPrefixOperator={false}
                />
                {volumeConstraints?.length > 1 ? (
                  <Button
                    onClick={() => handleRemove(index)}
                    disabled={disabled}
                    startIcon={<RemoveCircle />}
                    size="small"
                    sx={{ padding: 1 }}
                  >
                    Remove
                  </Button>
                ) : (
                  <Stack
                    sx={{
                      width: 96,
                    }}
                  />
                )}
              </Stack>
            );
          })}
        </Stack>
      </Stack>
      <Button
        startIcon={<AddCircleOutlined />}
        sx={{ maxWidth: 170, marginLeft: `${LABEL_WIDTH + 12}px` }}
        disabled={disabled}
        onClick={() => {
          handleAdd(true);
        }}
      >
        Add constraint
      </Button>
      <Stack
        direction="row"
        sx={{
          gap: 2,
        }}
      >
        <Typography
          sx={{
            width: LABEL_WIDTH,
            paddingTop: 1,
          }}
        >
          D
        </Typography>
        <Stack
          sx={{
            gap: 2,
          }}
        >
          {dConstraints?.map((constraint: OrganAtRiskConstraint) => {
            const index = oar.oarConstraints?.indexOf(constraint) || 0;
            return (
              <Stack
                direction="row"
                key={`${index}-d-constraint`}
                sx={{
                  gap: 2,
                }}
              >
                <OarConstraintRow
                  key={index}
                  index={index}
                  isVolume={false}
                  constraint={constraint}
                  disabled={disabled}
                  onChange={(field: string, value: string) =>
                    handleChange(index, field, value)
                  }
                  doseUnit={doseUnit}
                  showPrefixOperator={true}
                />
                {dConstraints?.length > 1 ? (
                  <Button
                    onClick={() => handleRemove(index)}
                    disabled={disabled}
                    startIcon={<RemoveCircle />}
                    size="small"
                    sx={{ padding: 1 }}
                  >
                    Remove
                  </Button>
                ) : (
                  <Stack
                    sx={{
                      width: 96,
                    }}
                  />
                )}
              </Stack>
            );
          })}
        </Stack>
      </Stack>
      <Button
        startIcon={<AddCircleOutlined />}
        sx={{ maxWidth: 170, marginLeft: `${LABEL_WIDTH + 12}px` }}
        onClick={() => {
          handleAdd(false);
        }}
        disabled={disabled}
      >
        Add constraint
      </Button>
    </Stack>
  );
};

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (oar: OrganAtRiskOut) => void;
  organAtRisk: OrganAtRiskOut;
  organOptions: string[];
  doseUnit: string;
  oarTitle?: string;
}

const OarDialog = ({
  open,
  onClose,
  onSubmit,
  organAtRisk,
  organOptions,
  doseUnit,
  oarTitle = "Add Organ At Risk Constraint",
}: Props) => {
  const [oar, setOar] = useState<OrganAtRisk>(EMPTY_ORGAN);
  const [organError, setOrganError] = useState(false);
  const VALUE_UNIT_OPTIONS = [doseUnit, "%"];
  useEffect(() => {
    if (open) {
      // Convert the number[] to string[] and the number to string
      const oarConstraints =
        organAtRisk?.oarConstraints?.map((constraint) => ({
          ...constraint,
          firstValue: constraint?.firstValue
            ? String(constraint?.firstValue)
            : null,
          secondValue: constraint?.secondValue?.map((value) => String(value)),
        })) || [];

      // Some pre-fill of empty data so the modal can work
      const volumeConstraintsIn = oarConstraints?.filter(
        (constraint) => constraint?.isVolumeData,
      );
      const dConstraintsIn = oarConstraints?.filter(
        (constraint) => !constraint?.isVolumeData,
      );

      const volumeConstraints = volumeConstraintsIn?.length
        ? volumeConstraintsIn.map(cleanOarConstraintData)
        : [
            {
              ...EMPTY_CONSTRAINT,
              isVolumeData: true,
            },
          ];
      const dConstraints = dConstraintsIn?.length
        ? dConstraintsIn.map(cleanOarConstraintData)
        : [
            {
              ...EMPTY_CONSTRAINT,
              isVolumeData: false,
            },
          ];

      // Convert the numbers to string in the organAtRisk
      const organAtRiskModal = {
        ...organAtRisk,
        maxValue: organAtRisk?.maxValue?.map((value) => String(value)),
        meanValue: organAtRisk?.meanValue?.map((value) => String(value)),
      };

      setOar({
        ...organAtRiskModal,
        oarConstraints: [...volumeConstraints, ...dConstraints],
      });
    }
  }, [organAtRisk, open]);

  useEffect(() => {
    if (!open) {
      // Clear state when dialog is closed
      setOar(EMPTY_ORGAN);
      setOrganError(false);
    }
  }, [open]);

  const isAlara = oar?.alara || false;
  const maxValue = oar?.maxValue?.join("/");
  const meanValue = oar?.meanValue?.join("/");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      data-testid="oar-modal"
      fullWidth
      maxWidth="md"
    >
      <DialogTitle sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {oarTitle}
      </DialogTitle>
      <DialogContent dividers>
        <Stack
          sx={{
            gap: 2,
            padding: 2,
          }}
        >
          <Stack
            direction="row"
            sx={{
              alignItems: "flex-start",
              gap: 2,
              paddingRight: 12,
            }}
          >
            <Typography
              sx={{
                paddingTop: 1,
                minWidth: LABEL_WIDTH,
              }}
            >
              Organ*
            </Typography>
            <Autocomplete
              size="small"
              data-testid="organ-select"
              value={oar?.organ || ""}
              options={organOptions}
              renderInput={(params) => (
                <TextField
                  data-testid="organ-select-value"
                  {...params}
                  error={organError}
                  helperText={organError ? "This field is required" : ""}
                />
              )}
              onChange={(_, value) => {
                if (!value) return;
                setOar({ ...oar, organ: value });
                setOrganError(false);
              }}
              disableClearable
              sx={{ width: "100%" }}
            />
          </Stack>
          <OarConstraint
            oar={oar}
            disabled={isAlara}
            onChange={(oar) => {
              setOar(oar);
            }}
            doseUnit={doseUnit}
          />
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              gap: 2,
            }}
          >
            <Stack
              direction="row"
              sx={{
                width: LABEL_WIDTH,
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Typography>Max</Typography>
              <Typography>≤</Typography>
            </Stack>
            <TextField
              data-testid="max-value"
              value={maxValue || ""}
              size="small"
              sx={{ width: 140 }}
              disabled={isAlara}
              onChange={(e) =>
                setOar({
                  ...oar,
                  maxValue: convertValue(e.target.value),
                })
              }
            />
            <Select
              value={oar?.maxValueUnit || ""}
              data-testid="max-value-unit"
              size="small"
              disabled={isAlara}
              onChange={(e) => setOar({ ...oar, maxValueUnit: e.target.value })}
              sx={{ minWidth: UNIT_WIDTH }}
            >
              {VALUE_UNIT_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </Stack>
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              gap: 2,
            }}
          >
            <Stack
              direction="row"
              sx={{
                width: LABEL_WIDTH,
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Typography>Mean</Typography>
              <Typography>≤</Typography>
            </Stack>
            <TextField
              data-testid="mean-value"
              value={meanValue || ""}
              size="small"
              sx={{ width: 140 }}
              disabled={isAlara}
              onChange={(e) =>
                setOar({
                  ...oar,
                  meanValue: convertValue(e.target.value),
                })
              }
            />
            <Select
              value={oar?.meanValueUnit || ""}
              data-testid="mean-value-unit"
              size="small"
              disabled={isAlara}
              onChange={(e) =>
                setOar({ ...oar, meanValueUnit: e.target.value })
              }
              sx={{ minWidth: UNIT_WIDTH }}
            >
              {VALUE_UNIT_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </Stack>
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                width: LABEL_WIDTH,
              }}
            >
              ALARA
            </Typography>
            <Switch
              data-testid="alara-switch"
              checked={isAlara}
              onChange={(_, value) => {
                setOar({ ...oar, alara: value });
              }}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => {
            if (!oar.organ) {
              setOrganError(true);
              return;
            }
            // Do some filtering to remove any empty constraints
            const oarConstraints = oar.oarConstraints?.filter(
              (constraint: OrganAtRiskConstraint) =>
                constraintIsNull(constraint) === false,
            );
            const updatedOar = {
              ...oar,
              oarConstraints,
            };
            // Convert the oar to the OrganAtRiskOut type
            const oarOut: OrganAtRiskOut = {
              ...updatedOar,
              maxValue: updatedOar.maxValue?.map((value) => Number(value)),
              meanValue: updatedOar.meanValue?.map((value) => Number(value)),
              oarConstraints: updatedOar.oarConstraints?.map((constraint) => ({
                ...constraint,
                firstValue: constraint.firstValue
                  ? Number(constraint.firstValue)
                  : null,
                secondValue: constraint.secondValue?.map((value) =>
                  Number(value),
                ),
              })),
            };
            onSubmit(oarOut);
          }}
          type="submit"
          data-testid="submit-btn"
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OarDialog;
